import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import axios from 'axios'
import { Button, Input, Label } from '@/components/ui'
import { Loader } from '@/components/common/Loader'
import { LiffErrorScreen } from '@/components/liff/LiffErrorScreen'
import { LiffShell } from '@/components/liff/LiffShell'
import {
  ensureLiffLoggedIn,
  initLiff,
  isLiffInClient,
  isLiffLoggedIn,
  LiffInitError,
  resolveLiffProfile,
} from '@/lib/liff'
import { credentialSignInAndBind, hasAuthTokens } from '@/modules/auth/service/liff'

export const Route = createFileRoute('/liff/login')({
  component: LiffLoginPage,
  beforeLoad: () => {
    if (hasAuthTokens()) {
      throw new Error('LIFF_REDIRECT_HOME')
    }
  },
  errorComponent: ({ error }) => {
    if (error instanceof Error && error.message === 'LIFF_REDIRECT_HOME') {
      window.location.href = '/liff'
      return null
    }
    return null
  },
})

function LiffLoginPage() {
  const navigate = useNavigate()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [booting, setBooting] = useState(true)
  const [initError, setInitError] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        await initLiff()
        if (isLiffInClient() && !isLiffLoggedIn()) {
          ensureLiffLoggedIn()
          return
        }
        if (!cancelled) setBooting(false)
      } catch (err) {
        if (!cancelled) {
          setInitError(
            err instanceof LiffInitError
              ? err.message
              : 'ไม่สามารถเชื่อมต่อ LINE ได้',
          )
          setBooting(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      await initLiff()
      const profile = await resolveLiffProfile()
      await credentialSignInAndBind(username, password, profile)
      navigate({ to: '/liff' })
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message ??
            'เข้าสู่ระบบไม่สำเร็จ กรุณาตรวจสอบชื่อผู้ใช้และรหัสผ่าน',
        )
      } else {
        setError(err instanceof Error ? err.message : 'เกิดข้อผิดพลาด')
      }
    } finally {
      setLoading(false)
    }
  }

  if (booting || loading) {
    return <Loader.FullScreen />
  }

  if (initError) {
    return <LiffErrorScreen message={initError} />
  }

  return (
    <LiffShell title="เชื่อมบัญชี LINE" hideNav>
      <div className="mx-auto max-w-md space-y-6">
        <p className="text-center text-base leading-relaxed text-slate-600">
          ครั้งแรกให้เข้าสู่ระบบด้วยชื่อผู้ใช้และรหัสผ่านของคุณ
          <br />
          ระบบจะผูกบัญชี LINE เพื่อเข้าใช้งานครั้งถัดไปโดยอัตโนมัติ
        </p>

        {error ? (
          <div className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        ) : null}

        <form
          className="space-y-5 rounded-2xl border border-border/50 bg-white p-6 shadow-sm"
          onSubmit={handleSubmit}
        >
          <div className="space-y-2">
            <Label htmlFor="liff-username" className="text-base">
              ชื่อผู้ใช้
            </Label>
            <Input
              id="liff-username"
              className="h-12 text-base"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="liff-password" className="text-base">
              รหัสผ่าน
            </Label>
            <Input
              id="liff-password"
              type="password"
              className="h-12 text-base"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
          <Button type="submit" className="h-12 w-full text-base" disabled={loading}>
            เข้าสู่ระบบและเชื่อม LINE
          </Button>
        </form>
      </div>
    </LiffShell>
  )
}
